export interface NavigationClient {
	id: number;
	name: string;
	user_id?: number;
	status?: string;
}

export interface NavigationMerchant {
	id: number;
	client_id: number;
	name: string;
	status?: string;
}

export interface MerchantDestination {
	merchant: NavigationMerchant;
	client: NavigationClient;
}

export interface MerchantNeighbors {
	previous: MerchantDestination | null;
	next: MerchantDestination | null;
	client?: NavigationClient;
	position: number;
	total: number;
}

// Session-local cache. Load only the current client and the clients needed at its edges.
// Stable ID ordering keeps previous/next symmetric, independently of API response order.
export const createMerchantNavigation = (
	loadClients: () => Promise<NavigationClient[]>,
	loadMerchants: (clientId: number) => Promise<NavigationMerchant[]>,
) => {
	let clientsRequest: Promise<NavigationClient[]> | undefined;
	const merchantRequests = new Map<number, Promise<NavigationMerchant[]>>();
	const clients = () => {
		if (!clientsRequest) {
			clientsRequest = loadClients().then(items => items.filter(item => item.id > 0 && item.status !== "deleted").sort((a, b) => a.id - b.id)).catch(error => {
				clientsRequest = undefined;
				throw error;
			});
		}
		return clientsRequest;
	};
	const merchants = (clientId: number) => {
		let pending = merchantRequests.get(clientId);
		if (!pending) {
			pending = loadMerchants(clientId).then(items => items
				.filter(item => item.id > 0 && item.client_id === clientId && item.status !== "deleted")
				.sort((a, b) => a.id - b.id)).catch(error => {
				merchantRequests.delete(clientId);
				throw error;
			});
			merchantRequests.set(clientId, pending);
		}
		return pending;
	};

	return {
		async neighbors(current: NavigationMerchant): Promise<MerchantNeighbors> {
			const [clientList, currentMerchants] = await Promise.all([clients(), merchants(current.client_id)]);
			const clientIndex = clientList.findIndex(client => client.id === current.client_id);
			const client = clientList[clientIndex];
			// A merchant opened directly (including a deleted one) remains a valid starting point.
			const siblings = currentMerchants.some(item => item.id === current.id)
				? currentMerchants
				: [...currentMerchants, current].sort((a, b) => a.id - b.id);
			const index = siblings.findIndex(item => item.id === current.id);
			const neighbor = async (direction: -1 | 1): Promise<MerchantDestination | null> => {
				if (!client) return null;
				const sibling = siblings[index + direction];
				if (sibling) return { merchant: sibling, client };
				for (let i = clientIndex + direction; i >= 0 && i < clientList.length; i += direction) {
					const entries = await merchants(clientList[i].id);
					const merchant = direction === 1 ? entries[0] : entries.at(-1);
					if (merchant) return { merchant, client: clientList[i] };
				}
				return null;
			};
			const [previous, next] = await Promise.all([neighbor(-1), neighbor(1)]);
			return { previous, next, client, position: index + 1, total: siblings.length };
		},
	};
};
